import React, {useContext, useEffect, useState} from "react";

import {useTranslation} from "react-i18next";
import {PackagesCartItem} from "types/entities/PackagesCartItem";
import {ReservationDriver} from "types/entities/ReservationDriver";
import {PackageItem} from "types/entities/PackageItem";
import {useDispatch} from "useDispatch";

import Loader from "components/Loader";
import BookingLoginAction from "store/bookingLogin/BookingLoginAction";

import {Driver} from "types/entities/Driver";
import {useReservationTicketOptions} from "utils/hooks/useReservationTicketOptions";
import AccessTicketDriversAssignOption from "pages/Front/Booking/BookingLogin/components/ReservationDetailComponents/AccessTicketDriversAssignOption";
import {useTypedSelector} from "helpers/reducers";
import {ReservationDriverAssignOption} from "types/enums/specific/ReservationDriverAssignOption";
import AccessTicketDriversAssignUser from "pages/Front/Booking/BookingLogin/components/ReservationDetailComponents/AccessTicketDriversAssignUser";
import AccessTicketOptionStatusPill from "pages/Front/Booking/BookingLogin/components/ReservationDetailComponents/AccessTicketOptionStatusPill";
import AssignationPanelComponent from "pages/Front/Booking/BookingLogin/components/ReservationDetailComponents/AssignationPanelComponent";
import {useAssignCurrentUserToTicketHandler} from "utils/reservationUtils";
import AccessTicketDriverAssigned from "pages/Front/Booking/BookingLogin/components/ReservationDetailComponents/AccessTicketDriverAssigned";
import BookingLoginReservationButton from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/BookingLoginReservationButton";
import AccessTicketDriverUpsells from "pages/Front/Booking/BookingLogin/components/ReservationDetailComponents/AccessTicketDriverUpsells";
import {BookingLoginReservationDetailDriverRedesignContext} from "pages/Front/Booking/BookingLogin/BookingLoginReservationDetailRedesign";
import {
    AccessTicketDriversAssignationStatusDriver,
    AccessTicketDriversAssignationStatusItem,
    StatusCheck,
} from "pages/Front/Booking/BookingLogin/components/ReservationDetailComponents/AccessTicketDriversAssignationStatusDriver";
import FeatherIcon from "components/FeatherIcon";
import AccessTicketItemHeader from "pages/Front/Booking/BookingLogin/components/ReservationDetailComponents/AccessTicketItemHeader";
import AccessTickerDriverIcon from "pages/Front/Booking/BookingLogin/components/ReservationDetailComponents/AccessTickerDriverIcon";
import {useAccessTicketDriversAssignOptionForFamily} from "utils/hooks/useAccessTicketDriversAssignOptionForFamily";
import clsx from "clsx";

const AccessTicketReservationDriverItem = (props: {item: PackagesCartItem; i: number; personIndex: number}) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const context = useContext(BookingLoginReservationDetailDriverRedesignContext);
    const selectedReservation = useTypedSelector(state => state.bookingLoginState.selectedReservation);

    const accessTicketDriversAssignOptionForFamily = useAccessTicketDriversAssignOptionForFamily(selectedReservation);

    const [packageItem, setPackageItem] = useState<PackageItem | null>(props.item.packageItem ?? null);
    const [reservationDriver, setReservationDriver] = useState<ReservationDriver | null>(null);
    const sessionDriver = useTypedSelector(state => state.bookingLoginState.user);
    const [isAssignationPanelOpen, setIsAssignationPanelOpen] = useState(false);
    const [selectedAssignationOption, setSelectedAssignationOption] = useState<ReservationDriverAssignOption | null>(null);

    const {fetchReservationAssignDriver, isLoading} = useAssignCurrentUserToTicketHandler();
    const openAssignmentHandler = (option: ReservationDriverAssignOption) => {
        setIsAssignationPanelOpen(false);
        setSelectedAssignationOption(option);
    };

    const closeAssignmentHandler = () => {
        setIsAssignationPanelOpen(true);
        setSelectedAssignationOption(null);
    };

    const assignCurrentUserToTicketHandler = async (driver: Driver): Promise<void> => {
        if (!context || !selectedReservation) {
            return;
        }

        try {
            const r = await fetchReservationAssignDriver(driver.id, props.item.id, selectedReservation.id, props.personIndex);

            if (r) {
                setReservationDriver(r.reservationDriver);
                // setIsAssignationPanelOpen(false);
                if (sessionDriver && sessionDriver.id == driver.id && (!driver?.nickname || !driver?.birthdate)) {
                    console.log("assignCurrentUserToTicketHandler -> setSelectedAssignationOption");
                    setSelectedAssignationOption(ReservationDriverAssignOption.AssignGuest);
                }
            }
        } catch (e) {
            console.log(e);
        }
    };

    const openEditTicketAssignmentHandler = () => {
        if (!isAssignationPanelOpen) {
            setSelectedAssignationOption(null);
        }
        setIsAssignationPanelOpen(!isAssignationPanelOpen);
    };

    const {options, getOption} = useReservationTicketOptions();

    const removeReservationDriverHandler = async () => {
        if (reservationDriver) {
            dispatch(BookingLoginAction.fetchRemoveReservationDriverFromReservation(reservationDriver.id)).then(r => {
                if (r) {
                    setReservationDriver(null);
                }
            });
        }
    };
    const getReservationDriverHandler = (packagesCartItem: PackagesCartItem) => {
        if (!selectedReservation) {
            console.log("no selectedReservation", selectedReservation);
            return;
        }
        const reservationDrivers = selectedReservation.reservationDrivers;
        if (!reservationDrivers) {
            // console.log("no reservationDrivers", reservationDrivers);
            return;
        }
        const reservationDriver = reservationDrivers.find(res => res.id == packagesCartItem.reservation_driver_id);

        if (!reservationDriver) {
            setReservationDriver(null);
            // console.log("no reservationDriver", reservationDriver, packagesCartItem);
            return;
        }
        setReservationDriver(reservationDriver);
    };

    useEffect(() => {
        if (props.item) {
            setPackageItem(props.item.packageItem ?? null);
            getReservationDriverHandler(props.item);
        }
    }, [props.item, selectedReservation]);

    if (!packageItem) {
        return <Loader />;
    }

    return (
        <div className={clsx(
            "AccessTicketReservationDriverItem bg-white md:rounded-[10px] overflow-hidden border-t md:border-0 border-gray-200 shadow-card",
            "PackageItemPersonType-" + packageItem?.person_type,
            "ReservationDriverPersonType-" + reservationDriver?.person_type || "undefined",
        )}>
            <AccessTicketItemHeader
                isAssignationPanelOpen={isAssignationPanelOpen}
                openEditTicketAssignmentHandler={openEditTicketAssignmentHandler}
                i={props.i}
                reservationDriver={reservationDriver}
                packageItem={packageItem}
            />

            {selectedAssignationOption && context && (
                <AssignationPanelComponent
                    item={props.item}
                    onCloseClick={closeAssignmentHandler}
                    option={selectedAssignationOption}
                    reservationDriver={reservationDriver}
                    setReservationDriver={setReservationDriver}
                    packageItem={packageItem}
                    personIndex={props.personIndex}
                />
            )}

            {!selectedAssignationOption && isAssignationPanelOpen && reservationDriver && (
                <div
                    className={
                        "AccessTicketDriversAssign flex flex-col items-start justify-center gap-x-1.5 w-full px-[17px] md:px-[81px] pb-[49px] md:pb-[58px] pt-[20px] md:pt-[28px]"
                    }
                >
                    <div className={"flex flex-row items-center justify-between w-full"}>
                        <h3 className={"font-semibold text-xs md:text-base pb-[20px] md:pb-[27px] text-black"}>{t("Assigned user")}</h3>
                        <BookingLoginReservationButton
                            onClick={removeReservationDriverHandler}
                            className={"bg-arbo-amber group focus:bg-arbo-red active:bg-arbo-red hover:bg-arbo-red rounded-full mb-[20px] md:mb-[27px]"}
                        >
                            <div
                                className={
                                    "font-medium group-focus:text-white group-hover:text-white  text-black text-xxs md:text-sm  flex flex-row items-center justify-center py-1 px-[10px]"
                                }
                            >
                                <span>{t("Remove guest")}</span>
                            </div>
                        </BookingLoginReservationButton>
                    </div>
                    <div className={"AccessTicketDriverAssignedContainer flex flex-col w-full gap-3"}>
                        <AccessTicketDriverAssigned
                            driver={reservationDriver}
                            onClick={() => openAssignmentHandler(ReservationDriverAssignOption.AssignGuest)}
                            isInvited={!!reservationDriver.invited_at}
                            isLoading={isLoading}
                        />
                    </div>
                    <div className={"AccessTicketDriverUpsellsContainer w-full mt-8"}>
                        <AccessTicketDriverUpsells driver={reservationDriver} />
                    </div>
                </div>
            )}

            {!selectedAssignationOption && isAssignationPanelOpen && !reservationDriver && (
                <div
                    className={
                        "AccessTicketDriversAssign flex flex-col items-start justify-center w-full px-[17px] md:px-[81px] pb-[49px] md:pb-[58px] pt-[20px] md:pt-[28px]"
                    }
                >
                    <h3 className={"font-semibold text-xs md:text-base pb-[20px] md:pb-[27px] text-black"}>{t("Assign user")}</h3>
                    <div
                        className={
                            "AccessTicketDriversAssignUserContainer flex flex-col md:columns-2 md:grid md:grid-cols-2 w-full gap-y-[20px] md:gap-y-[32px] gap-x-[20px] md:gap-x-[54px]"
                        }
                    >
                        {sessionDriver && !context?.isSessionDriverAssigned && (
                            <AccessTicketDriversAssignUser
                                driver={sessionDriver}
                                isLoading={isLoading}
                                onClick={() => assignCurrentUserToTicketHandler(sessionDriver)}
                            />
                        )}

                        {options.map((option, i) => {
                            const isFamilyOption = option == ReservationDriverAssignOption.AssignFamily;
                            const optionClass = accessTicketDriversAssignOptionForFamily.className;
                            const familyLabel = accessTicketDriversAssignOptionForFamily.label;

                            return (
                                <AccessTicketDriversAssignOption
                                    key={i}
                                    className={isFamilyOption ? optionClass : ""}
                                    isLoading={isLoading}
                                    option={option}
                                    // subLabel={familyCount > 0 ? familyCountLabel : null}
                                    subLabel={isFamilyOption ? familyLabel : null}
                                    onClick={() => openAssignmentHandler(option)}
                                />
                            );
                        })}
                    </div>
                </div>
            )}
            {!selectedAssignationOption && !isAssignationPanelOpen && (
                <div className={"AccessTicketDriversAssignationStatus relative cursor-pointer"} onClick={openEditTicketAssignmentHandler}>
                    <AccessTickerDriverIcon reservationDriver={reservationDriver} packageItem={packageItem} />

                    {reservationDriver && <AccessTicketDriversAssignationStatusDriver reservationDriver={reservationDriver} />}
                    {!reservationDriver && (
                        <div className="reservationDriverEmpty flex flex-col items-stretch justify-center w-full pl-[51px] md:pl-[81px] pr-[12px] md:pr-[16px] pt-[27px] md:pt-[33px] pb-[27px] md:pb-[33px]">
                            <AccessTicketDriversAssignationStatusItem>
                                <AccessTicketOptionStatusPill className={"bg-arbo-amber/50"}>
                                    <span className={"font-normal"}>{t("Assign ticket or invite a guest")}</span>
                                </AccessTicketOptionStatusPill>
                                <div className={"grow-0 flex flex-row gap-x-3 items-center justify-end"}>
                                    <StatusCheck>
                                        <FeatherIcon className={"size-[20px] md:size-[30px] text-arbo-amber"} name={"alert-circle"} strokeWidth={2} />
                                    </StatusCheck>
                                </div>
                            </AccessTicketDriversAssignationStatusItem>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AccessTicketReservationDriverItem;
